import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CurrencySelect } from "@/components/ui/currency-select";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { personaService, PersonaDefinition } from "../services/personaService";
import { organizationService } from "../services/organizationService";
import { useToast } from "@/hooks/use-toast";
import {
  User,
  Bell,
  Palette,
  Users,
  Mail,
  Smartphone,
  Brain,
  Loader2,
  Sun,
  Moon,
  Monitor,
  UserCog,
  Building2,
  Key,
  Eye,
  EyeOff,
} from "lucide-react";
import UsersTab from "../components/UsersTab";
import RoleChangeDialog from "../components/RoleChangeDialog";

const Settings = () => {
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    dealUpdates: true,
    riskAlerts: true,
    weeklyReports: false,
  });

  const [personas, setPersonas] = useState<PersonaDefinition[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(true);
  const [savingPersonas, setSavingPersonas] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingAppearance, setSavingAppearance] = useState(false);

  const [savingOrganization, setSavingOrganization] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState("INR");
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [loadingTeamAction, setLoadingTeamAction] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("organization");
  const [showRoleChangeDialog, setShowRoleChangeDialog] = useState(false);

  // Change Password dialog state
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwErrors, setPwErrors] = useState<{ current?: string; new?: string; confirm?: string }>({});
  const [changingPassword, setChangingPassword] = useState(false);

  const navigate = useNavigate();
  const { user, changePassword, logout, refreshUserData } = useAuth();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();

  // Validate active tab based on user role
  useEffect(() => {
    if (user && user.role !== 'ADMIN' && (activeTab === 'personas' || activeTab === 'users' || activeTab === 'organization')) {
      setActiveTab('profile');
    }
  }, [user, activeTab]);

  // Load personas + selections
  useEffect(() => {
    const loadPersonaData = async () => {
      try {
        setLoadingPersonas(true);
        const personaDefinitions = await personaService.getPersonaDefinitions();
        if (!personaDefinitions || personaDefinitions.length === 0) {
          toast({
            title: "No Personas Available",
            description: "No legal personas are currently configured in the system.",
            variant: "destructive",
          });
          setPersonas([]);
          setSelectedPersonas([]);
          return;
        }
        const userSelectedPersonas = await personaService.getUserSelectedPersonas();
        setPersonas(personaDefinitions);
        setSelectedPersonas(userSelectedPersonas);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to load persona data";
        toast({ title: "Loading Failed", description: errorMessage, variant: "destructive" });
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadPersonaData();
  }, [toast]);

  // Initialize organization data from user context
  useEffect(() => {
    if (user && user.organization) {
      setDefaultCurrency(user.organization.defaultCurrency || 'INR');
    }
  }, [user]);

  // Handle persona selection change
  const handlePersonaToggle = (personaType: string, checked: boolean) => {
    setSelectedPersonas((prev) => (checked ? [...prev, personaType] : prev.filter((p) => p !== personaType)));
  };

  const saveProfilePreferences = async () => {
    try {
      setSavingProfile(true);
      await new Promise((r) => setTimeout(r, 800));
      toast({ title: "Profile Updated", description: "Your profile information has been saved successfully." });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save profile";
      toast({ title: "Save Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setSavingProfile(false);
    }
  };

  const saveNotificationPreferences = async () => {
    try {
      setSavingNotifications(true);
      await new Promise((r) => setTimeout(r, 600));
      toast({ title: "Notifications Updated", description: "Your notification preferences have been saved successfully." });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save notifications";
      toast({ title: "Save Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setSavingNotifications(false);
    }
  };

  const savePersonaPreferences = async () => {
    try {
      setSavingPersonas(true);
      await personaService.updateUserSelectedPersonas(selectedPersonas);
      toast({
        title: "Preferences Saved",
        description: `Successfully updated your persona preferences (${selectedPersonas.length} personas selected).`,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save preferences";
      toast({ title: "Save Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setSavingPersonas(false);
    }
  };

  // Save organization settings
  const saveOrganizationSettings = async () => {
    try {
      setSavingOrganization(true);
      
      const updateData = {
        defaultCurrency: defaultCurrency,
      };

      await organizationService.updateOrganization(updateData);

      // Refresh user data to get updated organization info
      await refreshUserData();

      toast({
        title: "Organization Settings Saved",
        description: "Successfully updated default currency.",
      });
    } catch (error) {
      console.error("Error saving organization settings:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save organization settings";

      toast({
        title: "Save Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSavingOrganization(false);
    }
  };

  // Save all preferences
  const saveAllPreferences = async () => {
    try {
      setSavingProfile(true);
      setSavingNotifications(true);
      setSavingPersonas(true);
      if (user?.role === 'ADMIN') {
        setSavingOrganization(true);
      }

      const promises = [
        // Profile save would go here
        new Promise((resolve) => setTimeout(resolve, 800)),
        // Notification save would go here
        new Promise((resolve) => setTimeout(resolve, 600)),
        // Persona save
        personaService.updateUserSelectedPersonas(selectedPersonas),
      ];

      // Add organization save for admin users
      if (user?.role === 'ADMIN') {
        promises.push(
          organizationService.updateOrganization({
            defaultCurrency: defaultCurrency,
          })
        );
      }

      // Save all preferences in parallel
      await Promise.all(promises);

      // Refresh user data to get updated organization info (for admin users)
      if (user?.role === 'ADMIN') {
        await refreshUserData();
      }

      toast({
        title: "All Preferences Saved",
        description: "All your settings have been saved successfully.",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save some preferences";
      toast({ title: "Save Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setSavingProfile(false);
      setSavingNotifications(false);
      setSavingPersonas(false);
      if (user?.role === 'ADMIN') {
        setSavingOrganization(false);
      }
    }
  };

  const handleThemeChange = async (newTheme: "light" | "dark" | "system") => {
    try {
      setSavingAppearance(true);
      setTheme(newTheme);
      await new Promise((r) => setTimeout(r, 300));
      toast({ title: "Theme Updated", description: `Theme changed to ${newTheme}.` });
    } catch {
      toast({ title: "Theme Save Failed", description: "Failed to save theme preference.", variant: "destructive" });
    } finally {
      setSavingAppearance(false);
    }
  };

  // -------- Change Password dialog logic --------
  const pwStrong = (val: string) =>
    val.length >= 8 && /[a-z]/.test(val) && /[A-Z]/.test(val) && /\d/.test(val);

  const submitChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: typeof pwErrors = {};
    if (!pwCurrent.trim()) errs.current = "Current password is required";
    if (!pwNew) errs.new = "New password is required";
    else if (!pwStrong(pwNew)) errs.new = "Min 8 chars with uppercase, lowercase, and a number";
    if (!pwConfirm) errs.confirm = "Please confirm your new password";
    else if (pwConfirm !== pwNew) errs.confirm = "Passwords do not match";
    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      setChangingPassword(true);
      await changePassword(pwCurrent, pwNew);
      // Backend invalidates sessions; immediately log out client & redirect.
      await logout();
      // Redirect to login
      navigate("/login", { replace: true });
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Password change failed";
      toast({ title: "Change Failed", description: msg, variant: "destructive" });
    } finally {
      setChangingPassword(false);
      setShowPwDialog(false);
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwErrors({});
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    }
  };

  const handleRoleChangeSuccess = () => {
    toast({ title: "Request Submitted", description: "Your role change request has been submitted for review." });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-background to-gray-100/50 dark:from-gray-900 dark:via-background dark:to-gray-800/50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-foreground">Settings</h1>
          <p className="text-sm sm:text-base text-muted-foreground font-light">Manage your account preferences and application settings</p>
        </div>

        {/* Tabbed Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${user?.role === 'ADMIN' ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-3'} gap-1`}>
             {user?.role === 'ADMIN' && (
   <TabsTrigger value="organization" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Organization</span>
                  <span className="sm:hidden">Org</span>
                </TabsTrigger>
             )}
            <TabsTrigger value="profile" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <User className="w-3 h-3 sm:w-4 sm:h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Notifications</span>
              <span className="sm:hidden">Notifs</span>
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Palette className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Appearance</span>
              <span className="sm:hidden">Theme</span>
            </TabsTrigger>
            {user?.role === "ADMIN" && (
              <>
                <TabsTrigger value="personas" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Brain className="w-3 h-3 sm:w-4 sm:h-4" />
                  Personas
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                  Users
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-light">
                  <User className="w-5 h-5" />
                  Profile Settings
                </CardTitle>
                <CardDescription className="font-light">
                  Update your personal information and preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="font-light">
                      First Name
                    </Label>
                    <Input id="firstName" defaultValue={user?.firstName || ""} className="font-light" placeholder="Enter your first name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="font-light">
                      Last Name
                    </Label>
                    <Input id="lastName" defaultValue={user?.lastName || ""} className="font-light" placeholder="Enter your last name" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-light">Email Address</Label>
                  <Input id="email" type="email" defaultValue={user?.email || ""} className="font-light" disabled />
                  <p className="text-xs text-muted-foreground">Email cannot be changed. Contact support if needed.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization" className="font-light">Organization</Label>
                  <Input id="organization" value={user?.organization?.name || "Your Organization"} className="font-light" disabled />
                </div>

                <div className="space-y-2">
                  <Label className="font-light">Role</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{user?.role?.replace("_", " ") || "User"}</Badge>
                    {user?.role !== "ADMIN" && (
                      <Button variant="outline" size="sm" className="font-medium" onClick={() => setShowRoleChangeDialog(true)}>
                        <UserCog className="w-4 h-4 mr-2" />
                        Request Role Change
                      </Button>
                    )}
                  </div>
                </div>

                {/* Profile save */}
                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <Button onClick={saveProfilePreferences} disabled={savingProfile} className="font-medium">
                    {savingProfile ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : ("Save Changes")}
                  </Button>

                  {/* NEW: Change Password trigger */}
                  <Button type="button" variant="outline" className="font-medium" onClick={() => setShowPwDialog(true)}>
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-light">
                  <Bell className="w-5 h-5" />
                  Notifications
                </CardTitle>
                <CardDescription className="font-light">
                  Configure how you receive updates and alerts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <Label className="font-light">Email Notifications</Label>
                      </div>
                      <p className="text-sm text-muted-foreground font-light">Receive notifications via email</p>
                    </div>
                    <Switch
                      checked={notifications.email}
                      onCheckedChange={(checked) => setNotifications((prev) => ({ ...prev, email: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-muted-foreground" />
                        <Label className="font-light">Push Notifications</Label>
                      </div>
                      <p className="text-sm text-muted-foreground font-light">Browser and mobile push notifications</p>
                    </div>
                    <Switch
                      checked={notifications.push}
                      onCheckedChange={(checked) => setNotifications((prev) => ({ ...prev, push: checked }))}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="font-light">Deal Updates</Label>
                      <p className="text-sm text-muted-foreground font-light">Status changes and progress updates</p>
                    </div>
                    <Switch
                      checked={notifications.dealUpdates}
                      onCheckedChange={(checked) => setNotifications((prev) => ({ ...prev, dealUpdates: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="font-light">Risk Alerts</Label>
                      <p className="text-sm text-muted-foreground font-light">High-priority risk and compliance alerts</p>
                    </div>
                    <Switch
                      checked={notifications.riskAlerts}
                      onCheckedChange={(checked) => setNotifications((prev) => ({ ...prev, riskAlerts: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="font-light">Weekly Reports</Label>
                      <p className="text-sm text-muted-foreground font-light">Summary of deals and insights</p>
                    </div>
                    <Switch
                      checked={notifications.weeklyReports}
                      onCheckedChange={(checked) => setNotifications((prev) => ({ ...prev, weeklyReports: checked }))}
                    />
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button onClick={saveNotificationPreferences} disabled={savingNotifications} className="font-medium">
                    {savingNotifications ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : ("Save Notification Settings")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-light">
                  <Palette className="w-5 h-5" />
                  Appearance
                </CardTitle>
                <CardDescription className="font-light">
                  Customize the look and feel of the application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label className="font-light">Theme</Label>
                  <p className="text-sm text-muted-foreground font-light">
                    Choose your preferred color scheme. System will automatically match your device settings.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Button
                      variant={theme === "light" ? "default" : "outline"}
                      size="lg"
                      onClick={() => handleThemeChange("light")}
                      disabled={savingAppearance}
                      className="h-16 sm:h-20 flex-col gap-2 sm:gap-3"
                    >
                      {savingAppearance && theme !== "light" ? <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" /> : <Sun className="w-5 h-5 sm:w-6 sm:h-6" />}
                      <span className="font-medium text-sm sm:text-base">Light</span>
                    </Button>
                    <Button
                      variant={theme === "dark" ? "default" : "outline"}
                      size="lg"
                      onClick={() => handleThemeChange("dark")}
                      disabled={savingAppearance}
                      className="h-16 sm:h-20 flex-col gap-2 sm:gap-3"
                    >
                      {savingAppearance && theme !== "dark" ? <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" /> : <Moon className="w-5 h-5 sm:w-6 sm:h-6" />}
                      <span className="font-medium text-sm sm:text-base">Dark</span>
                    </Button>
                    <Button
                      variant={theme === "system" ? "default" : "outline"}
                      size="lg"
                      onClick={() => handleThemeChange("system")}
                      disabled={savingAppearance}
                      className="h-16 sm:h-20 flex-col gap-2 sm:gap-3"
                    >
                      {savingAppearance && theme !== "system" ? <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" /> : <Monitor className="w-5 h-5 sm:w-6 sm:h-6" />}
                      <span className="font-medium text-sm sm:text-base">System</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization Tab - Only visible for ADMIN users */}
          {user?.role === 'ADMIN' && (
            <TabsContent value="organization" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-light">
                    <Building2 className="w-5 h-5" />
                    Organization Settings
                  </CardTitle>
                  <CardDescription className="font-light">
                    Manage your organization details and preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-light">
                      Organization Name
                    </Label>
                    <p className="text-base font-medium text-foreground">
                      {user?.organization?.name || "Your Organization"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="defaultCurrency" className="font-light">
                      Default Currency
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      This will be the default currency for new deals and financial calculations
                    </p>
                    <CurrencySelect
                      name="defaultCurrency"
                      value={defaultCurrency}
                      onValueChange={setDefaultCurrency}
                      placeholder="Select default currency"
                      showFlag={true}
                      showSymbol={true}
                    />
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={saveOrganizationSettings}
                      disabled={savingOrganization}
                      className="font-medium"
                    >
                      {savingOrganization ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Organization Settings"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Personas Tab - Only visible for ADMIN users */}
          {user?.role === 'ADMIN' && (
            <TabsContent value="personas" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-light">
                    <Brain className="w-5 h-5" />
                    Legal Personas
                  </CardTitle>
                  <CardDescription className="font-light">
                    Select which legal personas you want to use for due diligence analysis
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingPersonas ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span className="ml-2 font-light">Loading personas...</span>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {personas.map((persona) => (
                          <div
                            key={persona.type}
                            className="border rounded-lg p-4 space-y-3 cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={() => handlePersonaToggle(persona.type, !selectedPersonas.includes(persona.type))}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Checkbox
                                    id={persona.type}
                                    checked={selectedPersonas.includes(persona.type)}
                                    onCheckedChange={(checked) => handlePersonaToggle(persona.type, checked as boolean)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Label htmlFor={persona.type} className="font-medium cursor-pointer">
                                    {persona.name}
                                  </Label>
                                </div>
                                <p className="text-sm text-muted-foreground font-light mb-2">{persona.description}</p>
                                <div className="flex flex-wrap gap-1">
                                  {persona.focusAreas.slice(0, 3).map((area) => (
                                    <Badge key={area} variant="secondary" className="text-xs">
                                      {area.replace(/_/g, " ")}
                                    </Badge>
                                  ))}
                                  {persona.focusAreas.length > 3 && (
                                    <Badge variant="outline" className="text-xs">+{persona.focusAreas.length - 3} more</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            Selected: {selectedPersonas.length} of {personas.length} personas
                          </p>
                          <p className="text-xs text-muted-foreground font-light">
                            Choose the personas that best match your due diligence requirements
                          </p>
                        </div>
                        <Button onClick={savePersonaPreferences} disabled={savingPersonas} className="font-medium">
                          {savingPersonas ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : ("Save Preferences")}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Users Tab - Only ADMIN */}
          {user?.role === "ADMIN" && (
            <TabsContent value="users" className="space-y-6">
              <UsersTab />
            </TabsContent>
          )}
        </Tabs>

        {/* Global Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={saveAllPreferences}
            disabled={savingProfile || savingNotifications || savingPersonas || savingOrganization}
            className="font-medium"
          >
            {savingProfile || savingNotifications || savingPersonas || savingOrganization ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving All Changes...
              </>
            ) : (
              "Save All Changes"
            )}
          </Button>
        </div>
      </div>

      {/* Role Change Request Dialog */}
      <RoleChangeDialog
        open={showRoleChangeDialog}
        onOpenChange={setShowRoleChangeDialog}
        currentRole={user?.role || ""}
        onSuccess={handleRoleChangeSuccess}
      />

      {/* Change Password Dialog */}
<Dialog open={showPwDialog} onOpenChange={setShowPwDialog}>
  <DialogContent
    className="
      w-[95vw] max-w-[640px] p-0 overflow-hidden
      rounded-2xl shadow-2xl border border-border
      data-[state=open]:animate-in data-[state=open]:fade-in-0
      data-[state=open]:zoom-in-95
    "
  >
    <DialogHeader className="px-6 pt-6 pb-2">
      <DialogTitle className="text-xl">Change Password</DialogTitle>
      <DialogDescription className="text-sm text-muted-foreground">
        Enter your current password and choose a new one. You’ll be signed out on all devices.
      </DialogDescription>
    </DialogHeader>

    <form onSubmit={submitChangePassword}>
      <div className="px-6 pb-6">
        {/* Fields container */}
        <div className="grid gap-5">
          {/* Current password — full width */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrent ? "text" : "password"}
                placeholder="Enter current password"
                value={pwCurrent}
                onChange={(e) => {
                  setPwCurrent(e.target.value);
                  setPwErrors((p) => ({ ...p, current: "" }));
                }}
                className={`pr-10 ${pwErrors.current ? "border-red-500" : ""}`}
                autoFocus
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                onClick={() => setShowCurrent((s) => !s)}
                aria-label={showCurrent ? "Hide" : "Show"}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pwErrors.current && <p className="text-xs text-red-500">{pwErrors.current}</p>}
          </div>

          {/* New + Confirm — side-by-side on sm+ */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? "text" : "password"}
                  placeholder="Enter new password"
                  value={pwNew}
                  onChange={(e) => {
                    setPwNew(e.target.value);
                    setPwErrors((p) => ({ ...p, new: "" }));
                  }}
                  className={`pr-10 ${pwErrors.new ? "border-red-500" : ""}`}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNew((s) => !s)}
                  aria-label={showNew ? "Hide" : "Show"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Must include an uppercase, a lowercase, and a number.
              </p>
              {pwErrors.new && <p className="text-xs text-red-500">{pwErrors.new}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter new password"
                  value={pwConfirm}
                  onChange={(e) => {
                    setPwConfirm(e.target.value);
                    setPwErrors((p) => ({ ...p, confirm: "" }));
                  }}
                  className={`pr-10 ${pwErrors.confirm ? "border-red-500" : ""}`}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? "Hide" : "Show"}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {pwErrors.confirm && <p className="text-xs text-red-500">{pwErrors.confirm}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-muted/40 border-t flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowPwDialog(false)}
          className="sm:min-w-[120px]"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={changingPassword} className="sm:min-w-[160px]">
          {changingPassword ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Changing…
            </>
          ) : (
            "Change Password"
          )}
        </Button>
      </div>
    </form>
  </DialogContent>
</Dialog>

    </div>
  );
};

export default Settings;
